import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import type {EventStats, EventStatSeries} from '@Components/mystuff-event-promotion-page/mystuff-event-promotion-page.types';

export type PromotionMetricType = 'reach' | 'earnings' | 'registrations' | 'page_views';

/** What clicking each metric's card does. A metric without an entry renders a non-interactive card. */
export type PromotionMetricClickHandlers = Partial<Record<PromotionMetricType, VoidFunction>>;

export type PromotionMetricsRowSlots = [PromotionMetricType, PromotionMetricType, PromotionMetricType];

export interface PromotionMetricsRowProps {
  stats: EventStats;
  /** Which metric each of the row's 3 slots shows, left to right — lets Promote and Manage share the card
   * look while keeping their own metric sets. Defaults to the Promote page's set. */
  metrics?: PromotionMetricsRowSlots;
  /** Omit on a page with no destination for a click (e.g. Manage). */
  metricClickHandlers?: PromotionMetricClickHandlers;
  /** A ticketed event issues tickets, not registrations, so that card reads "Tickets sold". */
  isTicketedEvent?: boolean;
  className?: string;
}

/** The Promote page's own metric set — the row's default when a caller doesn't override `metrics`. */
export const DEFAULT_PROMOTION_ROW_METRICS: PromotionMetricsRowSlots = ['reach', 'earnings', 'registrations'];

/** The Promote page's set for an event that can never take money: page views fills the Earnings slot, so an
 * RSVP, free or non-ticketed event is never shown a real-looking "US$0.00" of revenue. */
export const PROMOTION_ROW_METRICS_WITHOUT_EARNINGS: PromotionMetricsRowSlots = ['reach', 'page_views', 'registrations'];

/** The width at which three cards stop fitting side by side (3 * 240px card floor + gaps). Below it the row
 * both drops to `StatCard`'s compact density and becomes a snap carousel with dot pagination — one measurement,
 * because both answer the same question: can the three cards share a line. */
export const metricsRowContainerQuery: ContainerQueryObject = {
  small: {
    minWidth: 0,
    maxWidth: 750,
  },
};

/** Matches the ticketing dashboard's own fallback (`EventTicketingStatsHelper::DEFAULT_CURRENCY`). */
export const DEFAULT_CURRENCY_CODE = 'USD';

/** `KeyboardEvent.key` for the space bar. A plain string rather than an `EventKeys` member: `EventKeys` is an
 * enum, and comparing it to the `string` that `KeyboardEvent.key` is trips `no-unsafe-enum-comparison`. */
export const SPACE_BAR_KEY = ' ';

export interface MetricCardContent {
  title: string;
  iconClassName: string;
  infoTooltip?: string;
  /** Whether free (PAYG) users get the premium-upsell blur+crown treatment on this card. */
  isPremiumGated: boolean;
  formatValue: (series: EventStatSeries) => string;
}
